import React, { MouseEventHandler, useState } from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import { useNavigate } from "react-router-dom";
import { AutoSizer } from "react-virtualized";
import { mobileBreakpoint } from "../../Services/config";
// API
import { GetHardwareMonitoringStatusBody } from "@intelligentlilli/api-layer";
// Utils
import { getAgeFromDobString } from "@intelligentlilli/lilli-utils";
import { format } from "date-fns";
import { SensorStatus } from "Services/Utils/newDeviceMonitoring/types";
// Components
import { useViewport } from "../ViewportProvider";
import ServiceUserData from "../ServiceUserData";
import CareId from "../CareId";
import SquareAvatar from "../SquareAvatar";
import SecondaryButton from "../SecondaryButton";
import Line from "../Line";
import SensorStatusTag from "../SensorStatusTag";
import LilliButton from "../DesignSystemComponents/LilliButton/LilliButton";
// Icons
import {
  DownArrow,
  EditProfileIcon,
  EyeIcon,
  UpArrow,
} from "../../Styles/Icons/DesignSystem";
import { AddIcon, AssignmentIcon } from "../../Styles/Icons";
import { BackIcon, ForwardIcon } from "../../Styles/Icons/DesignSystem";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

const getConditionsToRender = (conditions, widthAvailable) => {
  const spaceRequiredForEndNumber = 20;
  const gapAndPaddingPerCondition = 15;
  const characterWidth = 6;

  const calcRequredWidth = (conditions1) =>
    conditions1.length * gapAndPaddingPerCondition +
    spaceRequiredForEndNumber +
    conditions1.reduce((acc, condition) => acc + condition.length, 0) *
      characterWidth;

  if (widthAvailable < spaceRequiredForEndNumber) {
    return [];
  }

  const conditionsCopy = conditions ? [...conditions] : [];
  let widthRequired = calcRequredWidth(conditionsCopy);
  while (widthRequired > widthAvailable) {
    conditionsCopy.pop();
    widthRequired = calcRequredWidth(conditionsCopy);
  }
  const numberRemoved = conditions.length - conditionsCopy.length;

  if (numberRemoved) {
    if (numberRemoved === conditions.length) {
      conditionsCopy.push(`${numberRemoved} conditions`);
    } else {
      conditionsCopy.push(`+${numberRemoved}`);
    }
  }
  return conditionsCopy;
};

export { getConditionsToRender };

type CareDetailsProp = {
  label: string, 
  value: string,
}

const CareDetails = ({ label, value }: CareDetailsProp) => {
  return (
    <div className={`${styles.row} ${styles.margin}`}>
      <div className={styles.neutral7_text}>{label}</div>
      <div className={styles.grey_text}>
        {value}
      </div>
    </div>
  )
}


interface ServiceUserInfoProps {
  serviceUser: any;
  isInstallerOnly: boolean;
  sensorData: {
    loading: boolean;
    error: boolean;
    data: GetHardwareMonitoringStatusBody | null;
    getHardwareStatus: () => void;
    sensorStatus: SensorStatus;
  };
  changePage: (
    activeTabs?: {
      tab: string;
    },
    endDate?: Date,
    startDate?: Date,
    dateType?: string
  ) => void;
  goToNext: MouseEventHandler<HTMLButtonElement>;
  goToPrevious: MouseEventHandler<HTMLButtonElement>;
  setEditProfile?: MouseEventHandler<HTMLButtonElement>;
  setNewReport?: MouseEventHandler<HTMLButtonElement>;
  setEditNotes?: MouseEventHandler<HTMLButtonElement>;
  setNewFriendOrFamily?: MouseEventHandler<HTMLButtonElement>;
  hubId?: string;
}

const ServiceUserInfo = ({
  serviceUser,
  setEditProfile,
  setNewReport,
  setEditNotes,
  setNewFriendOrFamily,
  isInstallerOnly = false,
  sensorData,
  changePage,
  goToNext,
  goToPrevious,
  hubId,
}: ServiceUserInfoProps) => {
  const { width } = useViewport();
  const navigate = useNavigate();

  const onMobile = width <= mobileBreakpoint;

  const [expanded, setExpanded] = useState(false);

  const track = useAmplitudeTracking();

  const rawCareType = serviceUser?.serviceUserProperties?.careType;
  const rawConditions = serviceUser?.serviceUserProperties?.conditions;

  const hubInstallationDate = serviceUser?.ts;
  const hasConditions = !!rawConditions;
  const hasCareTypes = !!rawCareType;

  const conditions = hasConditions ? rawConditions?.split(", ") : [];
  const careTypes = hasCareTypes ? rawCareType.split(", ") : [];
  const DOB = serviceUser?.serviceUserProperties?.dateOfBirth;
  // Create a regex to ensure the date is in the format YYYY-MM-DD
  const dateRegex = new RegExp(
    /^(19|20)\d\d-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/
  );
  const dateIsValid = dateRegex.test(DOB);
  const age = dateIsValid
    ? getAgeFromDobString(serviceUser?.serviceUserProperties?.dateOfBirth)
    : "? yrs";

  const hasHardware =
    serviceUser?.serviceUserProperties?.installationStatus === "complete";

  const hasDOB = age != null;
  const careID =
    serviceUser?.serviceUserProperties?.mosaicID ||
    serviceUser?.serviceUserProperties?.careId ||
    serviceUser?.hubProperties?.mosaicId ||
    serviceUser?.hubProperties?.careID ||
    "Not set";
  const nextOfKin = serviceUser?.serviceUserProperties.nextOfKin || "None";
  const doctor = serviceUser?.serviceUserProperties.doctor || "None";

  const deviceTagOnClick = () => {
    track("Device Monitoring", {
      action: "tag_clicked",
    });
    return hasHardware
      ? changePage({ tab: "devices" })
      : navigate(`/install/link/${hubId}`);
  };

  return (
    <div
      className={`${
        expanded && onMobile ? styles.wrapper : styles.wrapper_collapsed
      }`}
    >
      <div className={styles.navigation_buttons}>
        <LilliButton
          variant="secondary"
          onClick={goToPrevious}
          leftIcon={<BackIcon />}
        >
          Previous user
        </LilliButton>
        <LilliButton
          variant="secondary"
          onClick={goToNext}
          rightIcon={<ForwardIcon width={17} />}
        >
          Next user
        </LilliButton>
      </div>
      <div className={styles.SUInfo}>
        <div className={styles.SUInfo_left}>
          <div className={styles.SUInfo_bio}>
            <SquareAvatar photo={serviceUser?.photo} data-private />
            <div className={styles.SUInfo_bio_name}>
              <div className={styles.header_container}>
                <h1 className={styles.su_name} data-private>
                  {hasDOB
                    ? serviceUser?.userstring + ", " + age
                    : serviceUser?.userstring}
                </h1>
                <SensorStatusTag
                  sensorStatus={sensorData?.sensorStatus}
                  sensorStatusLoading={sensorData?.loading}
                  onClick={deviceTagOnClick}
                />
                {!isInstallerOnly && (
                  <div className={styles.mobile_only}>
                    <SecondaryButton
                      className={styles.button}
                      onClick={setNewFriendOrFamily}
                      startIcon={<AddIcon />}
                    >
                      {"Invite family"}
                    </SecondaryButton>
                  </div>
                )}
              </div>
              <div className={styles.careId} data-private>
                <CareId careID={careID} />
              </div>
              {expanded && onMobile && (
                <div className={styles.grey_text}>Conditions:</div>
              )}
              {!expanded && (
                <AutoSizer>
                  {({ width }) => (
                    <div className={styles.mobile_conditions} data-private>
                      {getConditionsToRender(conditions, width).map(
                        (condition, index) => {
                          return (
                            <div
                              className={styles.mobile_conditions_condition}
                              key={index}
                            >
                              {condition}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </AutoSizer>
              )}
            </div>
          </div>
          <div className={styles.conditions} data-private>
            <ServiceUserData serviceUser={serviceUser} />
          </div>
        </div>

        {/* Larger than mobile view */}
        {!isInstallerOnly && (
          <div className={styles.buttons}>
            <SecondaryButton
              startIcon={<EditProfileIcon colour={theme.primary6} />}
              className={styles.button}
              onClick={setEditProfile}
            >
              Edit profile
            </SecondaryButton>

            {hasHardware && (
              <SecondaryButton
                startIcon={<AssignmentIcon />}
                className={styles.button}
                onClick={setNewReport}
              >
                New report
              </SecondaryButton>
            )}

            <SecondaryButton
              startIcon={<EyeIcon colour={theme.primary6} />}
              className={styles.button}
              onClick={setEditNotes}
            >
              View/Add notes
            </SecondaryButton>
            <SecondaryButton
              className={styles.button}
              onClick={setNewFriendOrFamily}
              startIcon={<AddIcon />}
            >
              {"Invite family"}
            </SecondaryButton>
          </div>
        )}

        <SecondaryButton
          className={styles.expand}
          onClick={() => {
            setExpanded((prevExpanded) => !prevExpanded);
          }}
        >
          {expanded && onMobile ? (
            <UpArrow color="#979797" width={16} height={10} />
          ) : (
            <DownArrow color="#979797" width={16} height={10} />
          )}
        </SecondaryButton>
      </div>
      {expanded && onMobile && (
        <>
          <div
            data-private
            className={styles.care_type_and_conditions_container}
          >
            <div className={styles.mobile_conditions_wrap}>
              {conditions.map((condition, index) => (
                <div className={styles.mobile_conditions_condition} key={index}>
                  {condition}
                </div>
              ))}
              {!hasConditions && (
                <div className={styles.none_listed}>None listed</div>
              )}
            </div>
            <div className={`${styles.grey_text} ${styles.margin_bottom}`}>
              Care type:
            </div>
            {hasCareTypes && (
              <div className={styles.mobile_conditions_wrap}>
                {careTypes.map((condition, index) => (
                  <div className={styles.mobile_conditions_care} key={index}>
                    {condition}
                  </div>
                ))}
              </div>
            )}
            {!hasCareTypes && (
              <div className={styles.none_listed}>None listed</div>
            )}
          </div>
        </>
      )}
      {!expanded && <div className={styles.animationSpacer} />}
      <div data-private className={styles.care_contacts_container}>
        <div className={styles.dark_grey_text}>Care contacts</div>
        <Line />
        <CareDetails label="Next of kin" value={nextOfKin} />
        <CareDetails label="Doctor" value={doctor} />
        <Line />
        <div
          className={`${styles.row} ${styles.thin_grey_text} ${styles.margin_top}`}
        >
          <div>Care ID: {careID || "unknown"}</div>
          <div>
            User created:{" "}
            {hubInstallationDate
              ? `${format(new Date(hubInstallationDate), "dd.MM.yyyy")}`
              : "unknown"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceUserInfo;
